/**
 * Das Protokoll für **ferne** Bots (`v2Prompt.md` §27, §28; Zyklus `V2-2a`).
 *
 * **Dies ist die Leitung, nicht der Weg im Prozess.** Ein Bot, der im Dienst selbst rechnet,
 * bekommt einen `DecisionContext` (`packages/bots`, ab `V2-2c`) — den Zustand als Objekt, ohne
 * ihn zu serialisieren. Nur ein ferner Bot bekommt diese Nachricht. Die Trennung ist nicht
 * Ordnungsliebe: in der Massensimulation fällt pro Zug eine Entscheidung, und 1000 Partien à 80
 * Zügen wären 80.000 Serialisierungen von 54 Figuren, für die niemand einen Nutzen hat.
 *
 * **Das Protokoll musste fast nicht entworfen werden.** `gameStateSchema` (für die
 * `STATE`-Nachricht) und `legalActionSchema` (seit Zyklus 11g, weil die Debug-Ansicht dem Admin
 * die legalen Aktionen auflistet) bestanden schon. Ein ferner Bot bekommt damit **genau das, was
 * ein Browser bekommt** — und kein Feld mehr. §28 („keine fremden versteckten Informationen")
 * ist dadurch erfüllt, ohne einen zweiten Filter zu bauen: die Projektion aus
 * `apps/server/src/realtime/projection.ts` läuft davor.
 *
 * **Die Vertrauensgrenze liegt hier** (§28). Ein ferner Bot ist grundsätzlich nicht
 * vertrauenswürdig, und die Antwort ist dagegen **durch ihre Form** geschützt:
 *
 * | §28 verlangt | wie es hier erreicht wird |
 * |---|---|
 * | keinen `GameState` direkt verändern | die Antwort hat **kein** Zustandsfeld. Was es nicht gibt, kann nicht gelesen werden |
 * | keine illegale Aktion erzwingen | die Antwort nennt nur eine **Action-ID**; der Server schlägt sie in seiner **eigenen** Liste nach (`findLegalAction`) |
 * | keine veraltete Entscheidung ausführen | `gameId`, `turnNumber` und `revision` reisen zurück und werden gegen den eigenen Stand gehalten |
 *
 * **Die Revision ist `turnNumber` und die höchste Ereignisnummer der Partie**, zu einer
 * Zeichenkette verbunden (`"42:117"`) — persistent, monoton, ohne neue Spalte
 * (`docs/BOT_ARCHITECTURE.md` 8.4). Hier ist sie absichtlich nur eine Zeichenkette: wie sie
 * gebildet wird, entscheidet der Server, und das Protokoll soll es nicht festschreiben.
 *
 * **Versioniert, weil ein Worker älter sein kann als der Dienst.** Eine fremde Version wird
 * abgewiesen, statt hoffnungsvoll gelesen zu werden.
 */
import { z } from 'zod';
import { botDefinitionSchema, botDiagnosticsSchema, decisionBudgetSchema } from './bots.js';
import { capturedPieceSchema, gameStateSchema, hexSchema, } from './game-state.js';
import { legalActionSchema } from './legal-actions.js';
import { gameIdSchema } from './rest-dtos.js';
/**
 * Die aktuelle Protokollversion. Erhöht sich, sobald sich eine Form unverträglich ändert.
 *
 * **`V2-8a` hat zwei Formen ergänzt und die Version *nicht* erhöht**, und das ist eine
 * Entscheidung: bis dahin hat dieses Protokoll **niemand gesprochen** — es gab keinen Runner,
 * der es benutzt, und keinen Worker, der eine Version 1 kennt. Eine Erhöhung ohne Gegenüber
 * wäre eine erfundene Geschichte, und die nächste Lesung hielte sie für eine echte
 * Migration. Ab `V2-8b` gibt es einen Sprecher; **ab dann** ist eine Formänderung eine
 * Erhöhung.
 */
export const BOT_PROTOCOL_VERSION = 1;
/**
 * Die **Suchgrenzen**, die der Aufrufer festgelegt hat (`V2-8b`) — nicht sein Wunsch.
 *
 * **Die Form ist `DecisionBudget` ohne die Zeit, und das ist der ganze Unterschied.**
 * `DecisionBudget` ist der Wunsch des Hosts und darf `maxTimeMs` nennen; was eine Suche
 * wirklich begrenzt, rechnet der **Treiber** daraus aus (**E77**: `maxDepth` ist eine
 * Obergrenze des *Aufrufers*, nicht des Schemas, und `maxNodes` fällt aus der Zeit heraus).
 * Abgeleitet statt abgeschrieben, damit es keine zweite Wahrheit über dieselben drei Felder
 * gibt — der **Wert** wird gerechnet, die **Form** geerbt.
 *
 * **Warum sie überhaupt reisen muss:** ein Worker, der sie aus `bot.budget` selbst herleitete,
 * wäre eine zweite Stelle, die diese Entscheidung trifft. Bei der ersten Abweichung rechnete er
 * tiefer oder flacher als der Dienst zugesagt hat — und das fiele niemandem auf.
 *
 * **`deadlineAt` steht hier nicht und kann es nicht** (`strictObject`): eine Suche, die nach
 * Wanduhrzeit abbricht, liefert auf einem anderen Rechner ein anderes Ergebnis (§12). Ein
 * Worker *ist* ein anderer Rechner — das ist genau der Fall, für den die Regel geschrieben
 * wurde. `maxTimeMs` fehlt aus demselben Grund: es ist die Angabe, aus der der Treiber rechnet,
 * nicht die, nach der gesucht wird.
 *
 * **Leer ist erlaubt und bedeutet unbegrenzt** — die Lage des Labors (§29).
 */
export const decisionLimitsSchema = decisionBudgetSchema.omit({ maxTimeMs: true });
const revisionSchema = z.string().min(1);
const playerIdSchema = z.string().min(1);
const actionIdSchema = z.string().min(1);
/**
 * Die Frage an einen fernen Bot (§27).
 *
 * **`gameConfig` fehlt, und das ist Absicht:** die Balancing-Konfiguration steckt als Snapshot
 * **im** Zustand (`state.ruleConfig`, `CLAUDE.md` Regel 3). Sie daneben mitzuschicken wäre eine
 * zweite Quelle, die abweichen kann — genau die Abweichung, gegen die Regel 3 gebaut ist.
 * Dasselbe gilt für §27s `turnId` (`state.turnNumber`) und die Phase (`state.status`).
 *
 * **`team` steht trotzdem eigens dabei**, obwohl es aus `state.players` ableitbar wäre: MaxN und
 * die Teamsuche (§22, §23) brauchen es in jedem Knoten, und eine Ableitung je Knoten wäre eine
 * Suche in einer Liste für etwas, das über die ganze Partie konstant ist.
 *
 * **Die Aktionsliste darf leer sein.** Eine Stellung ohne legale Aktion ist in V1 praktisch
 * unerreichbar (dafür müssten alle neun Figuren vollständig eingeschlossen sein), aber sie ist
 * keine Formverletzung — und ein Schema, das sie verbietet, würde einen Zustand abweisen, statt
 * ihn behandelbar zu machen. Was der Treiber dann tut, entscheidet `V2-2e`.
 */
export const decisionRequestSchema = z.strictObject({
    protocolVersion: z.literal(BOT_PROTOCOL_VERSION),
    gameId: gameIdSchema,
    revision: revisionSchema,
    turnNumber: z.number().int().min(1),
    player: playerIdSchema,
    team: z.string().min(1).optional(),
    bot: botDefinitionSchema,
    /**
     * Der **abgeleitete** Seed dieser Entscheidung (**E57**) — nicht der Seed der Partie.
     *
     * **Ohne ihn bricht ein Worker die Reproduzierbarkeit, und zwar still.** `createRandom` wird
     * je Aufruf neu aus dem Seed erzeugt; eine Strategie, die lost (`random`, jeder Gleichstand
     * in `greedy`), entscheidet ohne ihn anders als im Prozess. Dieselbe Partie spielte dann
     * zweimal verschieden — §12 verloren, ohne dass es auffällt. **Er gehört damit zu den
     * Angaben, die den Ausführungsort gleichgültig machen**, und ist die Zusage, die `V2-8b`
     * prüft: dieselbe Stellung, derselbe Seed, dieselbe Wahl — im Prozess wie im Worker.
     *
     * ⚠️ **Er enthält den Partie-Seed im Klartext** (`decisionSeed` in `packages/bots` setzt
     * `partieSeed:spieler:zugnummer` zusammen). Für `LOCAL_WORKER` ist das belanglos — der
     * Thread liegt im selben Prozessbaum und lädt dasselbe Bündel. Für einen **fernen** Worker
     * ist es eine offene Frage aus `V2-8`: wer den Partie-Seed hat, kann die
     * Zufallsauffüllung des Aufstellungsablaufs vorhersagen (§11), und das ist fremde verdeckte
     * Information im Sinne von §28. Ein Hash davor hilft **nicht** — `createRandom` benutzt
     * `hashSeed(seed)` als ganzen Anfangszustand, der Hash *ist* also der Generator. Die
     * Auflösung ist ein eigener Bot-Seed und damit eine Entscheidung über das Datenmodell;
     * sie steht in `TODO.md` bei `V2-8c`.
     */
    seed: z.string().min(1),
    /** Was die Suche kosten darf — **gerechnet vom Aufrufer** (**E77**), siehe `decisionLimitsSchema`. */
    limits: decisionLimitsSchema,
    /** Der **projizierte** Zustand — verdeckte Information ist vorher heraus (§28). */
    state: gameStateSchema,
    legalActions: z.array(legalActionSchema),
});
/**
 * Die Antwort eines fernen Bots (§27).
 *
 * **Sie nennt eine Action-ID und sonst nichts Verbindliches.** `diagnostics` sind freiwillig und
 * beeinflussen die Partie nicht (§36) — eine Antwort ohne sie ist vollständig.
 *
 * Die drei zurückgetragenen Angaben sind keine Zierde: der Server hält sie gegen seinen eigenen
 * Stand, und ohne eine von ihnen wäre eine veraltete Entscheidung nicht erkennbar (§28).
 */
export const botResponseSchema = z.strictObject({
    protocolVersion: z.literal(BOT_PROTOCOL_VERSION),
    gameId: gameIdSchema,
    revision: revisionSchema,
    turnNumber: z.number().int().min(1),
    actionId: actionIdSchema,
    diagnostics: botDiagnosticsSchema.optional(),
});
/**
 * Das Angebot der Engine für eine Aufstellung (`SetupOptions`, **E55**).
 *
 * **Eine Ableitung, keine neue Regel:** die Figuren kommen aus `capturedOf`, die Felder aus
 * `freeStartZoneCells`. Dass sie mitreisen und nicht beim Worker gerechnet werden, ist dieselbe
 * Entscheidung wie bei `legalActions`: **die Engine bietet an, der Bot wählt** (§1, §41). Ein
 * Worker, der sich sein Angebot selbst ausrechnet, wäre die zweite Regel-Engine, die das
 * oberste Gebot dieses Projekts verbietet.
 */
export const setupOptionsSchema = z.strictObject({
    pieces: z.array(capturedPieceSchema),
    cells: z.array(hexSchema),
});
/**
 * Die Frage nach einer **Aufstellung** an einen fernen Bot (Regelfrage **Nr. 47**).
 *
 * **§27 kennt sie nicht** — der Abschnitt entstand, bevor Nr. 47 die Aufstellung zu einer
 * Bot-Entscheidung machte (vorher wäre es die Zufallsauffüllung aus §11 gewesen). Ohne diese
 * Form müsste ein ferner Worker die Aufstellung **an der Ausführungsart vorbei** schicken, und
 * genau das hat `packages/bots/src/types.ts` bei `BotRunner` als Falle notiert.
 *
 * **Ohne `turnNumber` und ohne `revision`, und das ist dieselbe Begründung wie bei
 * `SetupContext`:** in `SETUP` ist niemand am Zug, und die Aufstellung ist **eine**
 * Entscheidung je Bot, nicht eine je Figur. Was eine veraltete Antwort auffängt, ist der Dienst
 * selbst: `placePiece` prüft jede einzelne Setzung gegen das aktuelle Brett, und eine Figur,
 * die schon steht, lässt sich nicht zweimal setzen.
 */
export const setupRequestSchema = z.strictObject({
    protocolVersion: z.literal(BOT_PROTOCOL_VERSION),
    gameId: gameIdSchema,
    player: playerIdSchema,
    team: z.string().min(1).optional(),
    bot: botDefinitionSchema,
    /** Der abgeleitete Seed der Aufstellung (`setupSeed`) — siehe `DecisionRequest.seed`. */
    seed: z.string().min(1),
    /**
     * Die Grenzen — **auch hier, obwohl heute keine Strategie sie in der Aufstellung liest.**
     *
     * `randomPlacement` kennt nur `options` und `seed`, und alle vier Strategien delegieren
     * dorthin. Trotzdem reisen sie: `BotRunner.decideSetup` bekommt im Prozess ein
     * `limits`-Argument, und es auf der Leitung wegzulassen wäre eine **stille** Abweichung
     * zwischen den beiden Wegen — genau die Art, gegen die diese Phase gebaut ist. Eine
     * **gesuchte** Aufstellung (ein eigener Zyklus) wäre fern sonst unbegrenzt.
     */
    limits: decisionLimitsSchema,
    /** Der **projizierte** Zustand: in `SETUP` sind die Figuren der anderen heraus (§45). */
    state: gameStateSchema,
    options: setupOptionsSchema,
});
/** Eine Setzung: diese Figur auf dieses Feld. Beides kommt aus dem Angebot, nichts daneben. */
export const setupPlacementSchema = z.strictObject({
    piece: z.string().min(1),
    to: hexSchema,
});
/**
 * Die Antwort auf eine Aufstellungsanfrage.
 *
 * **Anders als beim Zug nennt sie nicht eine ID, sondern eine Anordnung** — eine Aufstellung
 * *ist* eine Menge von Paaren, und es gibt keine Liste vorberechneter Anordnungen, aus der man
 * eine auswählen könnte (neun Figuren auf fünfzehn Felder sind über eine Milliarde). Die
 * Vertrauensgrenze bleibt deshalb trotzdem gewahrt, nur anders: der Dienst prüft **jede**
 * Setzung gegen sein eigenes Angebot (`requireCompletePlacement` in `packages/bots`) und
 * danach noch einmal gegen die Engine (`placeSetupPiece`).
 *
 * **Eine leere Anordnung ist eine Form und kein Fehler:** ein Bot, der alle Figuren gesetzt und
 * nur nicht bestätigt hat, bekommt ein leeres Angebot. Dieselbe Begründung wie bei
 * `legalActions` — ein Schema, das die Lage verbietet, weist einen Zustand ab, statt ihn
 * behandelbar zu machen.
 */
export const setupResponseSchema = z.strictObject({
    protocolVersion: z.literal(BOT_PROTOCOL_VERSION),
    gameId: gameIdSchema,
    player: playerIdSchema,
    placements: z.array(setupPlacementSchema),
    diagnostics: botDiagnosticsSchema.optional(),
});
//# sourceMappingURL=bot-protocol.js.map